import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useReducer, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  connectionRequiresUserLogin,
  ConnectionStatus,
  InstrumentServiceDetails,
} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails} from 'instruments/connections/ConnectionDAO';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT,
} from 'performance/processLabAutomationEvents';
import {FilterKey} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';
import * as DateUtil from 'util/DateUtil';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionFileStatus, getConnectionFileStatus} from '../connection-files/ConnectionFilesStatus';
import ConnectionFilesTable, {
  useConnectionIdsAndFiltersState,
} from '../connection-files/ConnectionFilesTable';
import reducer, {ImportInstrumentDataModalActionType} from './importInstrumentDataModalReducer';
import {
  ImportInstrumentDataModalState,
  InstrumentData,
  InstrumentFileStatus,
  InstrumentQueryStatus,
  isUnselectableFileStatus,
  StepKey,
  INITIAL_STATE,
} from './InstrumentDataConstants';
import {createCopyObjectInstrumentQuery, pollInstrumentQueryStatus, RunReference} from './InstrumentDataDAO';
import InstrumentDataTable from './InstrumentDataTable';
import InstrumentLoginPage from './InstrumentLoginPage';

interface CopyObjectResponseParams {
  path?: string;
}

export interface BrowseConnectionFilesSubmitResult {
  newFileNames: Array<string>;
  existingFiles: Array<FileListingGraphQL>;
}

export interface BrowseConnectionFilesModalProps {
  connectionId: string;
  runId?: string;
  allowMultiple: boolean;
  onRequestClose: () => void;
  onRequestSubmit: (result: BrowseConnectionFilesSubmitResult) => void | Promise<void>;
}

/**
 * Send COPY_OBJECT for each selected instrument file that isn't in Benchling yet, poll until done, and return the
 * filenames from the COPY_OBJECT response params.
 *
 * Files that are already UPLOADED are returned by name without a query: the file's latest query can be a refetch
 * that is still in progress (or never finishes if the gateway is offline), and the uploaded copy is usable now.
 */
export async function extractFileNames(
  state: ImportInstrumentDataModalState,
  run?: RunReference
): Promise<Array<string>> {
  const params: {[key: string]: string | boolean} = {};
  if (state.loginUsername) {
    params.username = state.loginUsername;
  }

  const selectedData = Array.from(state.selectedRowIndices.values()).map(
    (index) => state.instrumentData[index]
  );
  const uploadedData = selectedData.filter((datum) => datum.status === InstrumentFileStatus.UPLOADED);
  const dataToCopy = selectedData.filter((datum) => datum.status !== InstrumentFileStatus.UPLOADED);

  const queryResponses = await Promise.all(
    dataToCopy.map(async (datum) => {
      const {instrumentQueryId} = await createCopyObjectInstrumentQuery(datum, params, run);
      return pollInstrumentQueryStatus(instrumentQueryId);
    })
  );

  const fileNames = uploadedData.map((datum) => datum.displayFilename);
  for (const queryResponse of queryResponses) {
    if (queryResponse.status === InstrumentQueryStatus.DONE) {
      const filename = (queryResponse.params as CopyObjectResponseParams)?.path;
      if (filename) {
        fileNames.push(filename);
      }
    } else {
      notifications.error(`Failed to retrieve file: ${queryResponse.info || 'Unknown error'}`);
    }
  }
  return fileNames;
}

/**
 * Modal for browsing and selecting files from a non-auto-syncing connection.
 *
 * Shows two sections:
 * 1. "Retrieve files from connection" — instrument files available via the instrument service
 *    (BROWSE_FILES). Selecting these triggers COPY_OBJECT to retrieve them into the data catalog.
 * 2. "Previously retrieved files" — data catalog files already associated with this connection.
 *    These can be used directly without retrieval.
 *
 * Handles per-user login if the connection requires it.
 */
export default function BrowseConnectionFilesModal(props: BrowseConnectionFilesModalProps): JSX.Element {
  const {connectionId, onRequestClose} = props;
  // Reuse existing reducer for login + instrument data state
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  // Separate state for data catalog files
  const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);
  const [selectedConnectionFileIndices, setSelectedConnectionFileIndices] = useState<Set<number>>(new Set());

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState<InstrumentServiceDetails | null>(
    null
  );

  const isConnectionAvailable = instrumentServiceDetails?.status.value === ConnectionStatus.AVAILABLE;

  // Load instrument service details and determine if login is required
  useEffect(() => {
    getInstrumentServiceDetails(connectionId)
      .then((details) => {
        setInstrumentServiceDetails(details);
        if (!connectionRequiresUserLogin(details)) {
          dispatch({
            type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.PROCESS,
          });
        }
      })
      .catch(() => {
        notifications.error('Unable to load connection details. Please try again.');
        onRequestClose();
      });
  }, [connectionId, onRequestClose]);

  // Auto-advance to file selection after successful login
  useEffect(() => {
    if (state.currentStepKey === StepKey.LOGIN && state.loginQueryStatus === InstrumentQueryStatus.DONE) {
      dispatch({
        type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
        nextStepKey: StepKey.PROCESS,
      });
    }
  }, [state.loginQueryStatus, state.currentStepKey]);

  // ConnectionFilesTable filter state
  const {
    filtersState: [filters, setFilters],
  } = useConnectionIdsAndFiltersState({
    initialConnectionIds: [props.connectionId],
    initialFilters: null,
  });

  const setInstrumentDataCallback = useCallback(
    (instrumentData: Array<InstrumentData>): void => {
      dispatch({type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_DATA, instrumentData});
    },
    [dispatch]
  );

  const setConnectionFilesCallback = useCallback(
    (files: Array<FileListingGraphQL>): void => {
      setConnectionFiles(files);
      setSelectedConnectionFileIndices(new Set());
    },
    [setConnectionFiles]
  );

  const toggleConnectionFileSelection = useCallback((rowIndex: number): void => {
    setSelectedConnectionFileIndices((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  }, []);

  const totalSelectedCount = state.selectedRowIndices.size + selectedConnectionFileIndices.size;

  const steps = useMemo(() => {
    const stepsList: Array<{key: StepKey; title: string}> = [];
    if (instrumentServiceDetails?.fields.per_user_login_required?.value === 'true') {
      stepsList.push({key: StepKey.LOGIN, title: 'Login'});
    }
    stepsList.push({key: StepKey.PROCESS, title: 'Select files'});
    return stepsList;
  }, [instrumentServiceDetails]);

  const handleSubmit = async (): Promise<void> => {
    dispatch({type: ImportInstrumentDataModalActionType.IS_IMPORTING, isImporting: true});
    try {
      let newFileNames: Array<string> = [];
      if (state.selectedRowIndices.size > 0) {
        logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT);
        newFileNames = await extractFileNames(state, props.runId == null ? undefined : {runId: props.runId});
        logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT);
      }

      const existingFiles = [...selectedConnectionFileIndices].map((index) => connectionFiles[index]);

      await props.onRequestSubmit({newFileNames, existingFiles});
    } finally {
      dispatch({type: ImportInstrumentDataModalActionType.IS_IMPORTING, isImporting: false});
    }
  };

  const getButtons = (): Array<ModalButton> => {
    if (state.currentStepKey === StepKey.LOGIN) {
      return [
        {
          text: 'Next',
          disabled: state.loginQueryStatus !== InstrumentQueryStatus.DONE,
          submit: true,
          onClick: () => {
            dispatch({
              type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
              nextStepKey: StepKey.PROCESS,
            });
          },
        },
      ];
    }
    return [
      {
        text: state.isImporting ? 'Processing...' : 'Process',
        disabled: state.isImporting || totalSelectedCount === 0,
        submit: true,
        onClick: handleSubmit,
      },
    ];
  };

  // Loading state while instrument service details are being fetched
  if (!instrumentServiceDetails) {
    return (
      <Modal
        closeableOnEscapeKey={false}
        large={true}
        onRequestClose={props.onRequestClose}
        title="Retrieve data from connection"
      >
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }

  const helpText =
    state.currentStepKey === StepKey.LOGIN
      ? 'Login to access your files.'
      : `Select files to retrieve and process.${
          instrumentServiceDetails.id != null
            ? ` Connection last updated on ${DateUtil.formalTime(instrumentServiceDetails.status.timestamp)}.`
            : ''
        }`;

  return (
    <Modal
      buttons={getButtons()}
      closeableOnEscapeKey={false}
      large={true}
      onRequestClose={props.onRequestClose}
      title="Retrieve data from connection"
    >
      <StepNav<StepKey>
        steps={steps}
        statusByStep={state.statusByStep}
        currentStepKey={state.currentStepKey}
        onUpdateCurrentStep={undefined}
        variant={StepNavVariant.FLAT}
      />
      {!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS &&
        instrumentServiceDetails &&
        !isConnectionAvailable && (
          <div className={css(styles.warningBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text={
                instrumentServiceDetails.status.value === ConnectionStatus.DISABLED
                  ? 'This connection is disabled. Please re-enable it to retrieve the latest data.'
                  : 'This connection is not currently available. Please try again later for the latest data.'
              }
            />
          </div>
        )}
      <p className={css(styles.helpText)}>{helpText}</p>
      {state.currentStepKey === StepKey.LOGIN ? (
        <InstrumentLoginPage connectionId={props.connectionId} dispatch={dispatch} state={state} />
      ) : (
        <div>
          <h4 className={css(styles.sectionHeader)}>Retrieve files from connection</h4>
          <p className={css(styles.sectionDescription)}>
            Select files to retrieve from the connection. These files will be copied to the data catalog for
            processing.
          </p>
          <InstrumentDataTable
            instrumentData={state.instrumentData}
            setInstrumentData={setInstrumentDataCallback}
            selectedRowIndices={state.selectedRowIndices}
            onSelectRow={(rowIndex: number): void => {
              if (
                !isUnselectableFileStatus(state.instrumentData[rowIndex].status, isConnectionAvailable) &&
                (props.allowMultiple || totalSelectedCount === 0 || state.selectedRowIndices.has(rowIndex))
              ) {
                dispatch({
                  type: ImportInstrumentDataModalActionType.SELECT_ROW,
                  selectedRowIndex: rowIndex,
                });
              }
            }}
            allowMultiple={props.allowMultiple}
            columns={['File name', 'Status', 'Modified at', 'Refetch']}
            filterOptions={[]}
            initialFilterValues={{[FilterKey.CONNECTION]: [props.connectionId]}}
            isImporting={state.isImporting && state.selectedRowIndices.size > 0}
            isConnectionAvailable={isConnectionAvailable}
            loginUsername={state.loginUsername}
          />

          <h4 className={css(styles.sectionHeader)}>Previously retrieved files</h4>
          <p className={css(styles.sectionDescription)}>
            Files already in the data catalog for this connection. Select to use without re-retrieving.
          </p>
          <ConnectionFilesTable
            filters={filters}
            setFilters={setFilters}
            connectionFiles={connectionFiles}
            setConnectionFiles={setConnectionFilesCallback}
            selectedRowIndices={selectedConnectionFileIndices}
            onSelectRows={(rowIndices: Set<number>): void => {
              for (const rowIndex of rowIndices) {
                if (
                  getConnectionFileStatus(connectionFiles[rowIndex], true) ===
                    ConnectionFileStatus.UPLOADED &&
                  (props.allowMultiple ||
                    totalSelectedCount === 0 ||
                    selectedConnectionFileIndices.has(rowIndex))
                ) {
                  toggleConnectionFileSelection(rowIndex);
                }
              }
            }}
            allowMultiple={props.allowMultiple}
            columns={['File name', 'Status', 'Modified at', 'Created at', 'File path']}
          />
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  helpText: {
    fontStyle: 'italic',
    padding: '10px 0 20px',
  },
  warningBanner: {
    marginTop: '10px',
  },
  sectionHeader: {
    marginTop: '16px',
    marginBottom: '4px',
    fontWeight: 600,
  },
  sectionDescription: {
    color: '#666',
    fontSize: '13px',
    marginBottom: '8px',
  },
});
